// src/webideal-jobs/pages/Dashboard.jsx

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { backendApi as base44 } from "@/api/backendApi";
import { useAuth } from "../../context/AuthContext";

import Sidebar from "../components/dashboard/Sidebar";
import SearchStatusWidget from "../components/dashboard/SearchStatusWidget";
import ScamWarningWidget from "../components/dashboard/ScamWarningWidget";
import JobCard from "../components/dashboard/JobCard";
import MyJobActivityWidget from "../components/dashboard/MyJobActivityWidget";
import RecruiterActivityWidget from "../components/dashboard/RecruiterActivityWidget";
import RecentApplicationsWidget from "../components/dashboard/RecentApplicationsWidget";
import RecentlyViewedJobsWidget from "../components/dashboard/RecentlyViewedJobsWidget";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { colors as DS, TechLabel } from "../../design-system";
import { ArrowRight, Sparkles } from "lucide-react";

const ACCENT = DS.product.jobs;

export default function Dashboard() {
  const { user } = useAuth();
  const [savedJobMap, setSavedJobMap] = useState({});
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(null);

  const { data: analytics } = useQuery({
    queryKey: ["dashboard", "analytics"],
    queryFn: () => base44.entities.Dashboard.analytics(),
    staleTime: 60000,
    enabled: !!base44.auth.getToken(),
  });

  const { data: jobs } = useQuery({
    queryKey: ["jobs", "recommended"],
    queryFn: () => {
      if (base44.auth.getToken()) {
        return base44.entities.Job.getRecommended();
      }
      return base44.entities.Job.list();
    },
  });

  useQuery({
    queryKey: ["user-data"],
    queryFn: async () => {
      if (!base44.auth.getToken()) return null;
      try {
        const [saved, apps] = await Promise.all([
          base44.entities.SavedJob.list(),
          base44.entities.Application.list(),
        ]);

        const sMap = {};
        saved.forEach((s) => (sMap[s.job?.id || s.job?._id || s.job] = s.id));
        setSavedJobMap(sMap);

        const appSet = new Set(apps.map((a) => a.job?.id || a.job?._id || a.job));
        setAppliedJobs(appSet);

        return { saved, apps };
      } catch (err) {
        console.error("Error loading user data:", err);
        return null;
      }
    },
  });

  const handleSaveJob = async (job) => {
    if (!base44.auth.getToken()) {
      alert("Please login to save jobs.");
      return;
    }
    try {
      if (savedJobMap[job.id]) {
        await base44.entities.SavedJob.delete(savedJobMap[job.id]);
        const newMap = { ...savedJobMap };
        delete newMap[job.id];
        setSavedJobMap(newMap);
      } else {
        const res = await base44.entities.SavedJob.create({ job: job.id });
        setSavedJobMap({ ...savedJobMap, [job.id]: res.id });
      }
    } catch (error) {
      alert(error.message || "Failed to save job");
    }
  };

  const handleApply = async (job) => {
    if (!base44.auth.getToken()) {
      alert("Please login to apply.");
      return;
    }
    if (appliedJobs.has(job.id)) return;

    setActionLoading(job.id);
    try {
      await base44.entities.Application.create({ job: job.id });
      setAppliedJobs(new Set([...appliedJobs, job.id]));
      alert("Successfully applied!");
    } catch (error) {
      alert(error.message || "Failed to apply");
    } finally {
      setActionLoading(null);
    }
  };

  const profilePct = analytics?.profileCompletion || 0;
  const initial = (user?.name || user?.full_name || "U").charAt(0).toUpperCase();

  return (
    <div
      className="w-full px-4 py-8"
      style={{
        background: DS.paper,
        color: DS.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      <div className="flex flex-col lg:flex-row gap-6">
        {/* SIDEBAR */}
        <div className="w-full lg:w-64">
          <Sidebar />
        </div>

        {/* MAIN */}
        <div className="flex-1 min-w-0">
          {/* PROFILE BANNER, blueprint with progress bar */}
          <div
            className="mb-6 ds-spatial relative overflow-hidden"
            style={{ background: DS.paper, border: `1px solid ${DS.hairline}`, borderLeft: `4px solid ${ACCENT}` }}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -top-12 -right-12 w-44 h-44 opacity-20 blur-3xl"
              style={{ background: ACCENT }}
            />
            <div className="relative p-6 flex flex-wrap items-center justify-between gap-5">
              <div className="flex items-center gap-4 min-w-0">
                <div
                  className="w-16 h-16 flex items-center justify-center text-2xl font-extrabold flex-shrink-0"
                  style={{ background: DS.forest, color: DS.paper }}
                >
                  {initial}
                </div>
                <div className="min-w-0">
                  <TechLabel style={{ color: ACCENT }}>welcome back</TechLabel>
                  <h2 className="text-xl md:text-2xl font-extrabold tracking-tight mt-0.5 truncate" style={{ color: DS.forest }}>
                    {user?.name || user?.full_name || "Job seeker"}
                  </h2>
                  <div className="flex items-center gap-3 mt-2.5 max-w-md">
                    <div className="flex-1 h-1.5 relative overflow-hidden" style={{ background: DS.hairline }}>
                      <div
                        className="absolute inset-y-0 left-0 transition-all duration-1000"
                        style={{ width: `${profilePct}%`, background: ACCENT }}
                      />
                    </div>
                    <TechLabel className="tabular-nums whitespace-nowrap" style={{ color: DS.forest }}>
                      {profilePct}% complete
                    </TechLabel>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="ds-skeu-btn-primary cursor-pointer h-11 px-5 text-xs font-bold uppercase inline-flex items-center"
                style={{ letterSpacing: "0.16em" }}
              >
                <Sparkles className="w-3.5 h-3.5 mr-2" />
                Complete profile
              </button>
            </div>
          </div>

          <MyJobActivityWidget />

          {/* RECOMMENDED JOBS */}
          <div className="mb-6 relative" style={{ background: DS.paper, border: `1px solid ${DS.hairline}` }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: DS.hairline }}>
              <div className="flex items-center gap-3">
                <span className="w-2 h-2" style={{ background: ACCENT }} />
                <div>
                  <TechLabel style={{ color: ACCENT }}>02 · for you</TechLabel>
                  <h2 className="text-lg font-bold leading-tight mt-0.5" style={{ color: DS.forest }}>
                    Recommended jobs
                  </h2>
                </div>
              </div>
              <button
                type="button"
                className="text-xs font-bold uppercase inline-flex items-center gap-1.5 transition-colors hover:text-[#1A3C2B]"
                style={{ letterSpacing: "0.16em", color: ACCENT }}
              >
                View all
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="px-6 pt-4">
              <Tabs defaultValue="profile">
                <TabsList
                  className="bg-transparent border-b w-full rounded-none justify-start gap-px"
                  style={{ borderColor: DS.hairline }}
                >
                  <TabsTrigger
                    value="profile"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 text-xs font-bold uppercase"
                    style={{ letterSpacing: "0.16em", color: DS.grid }}
                  >
                    Profile match ({jobs?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger
                    value="like"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 text-xs font-bold uppercase"
                    style={{ letterSpacing: "0.16em", color: DS.grid }}
                  >
                    You might like
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {jobs?.slice(0, 3).map((job, i) => (
                <JobCard
                  key={i}
                  job={job}
                  onSave={handleSaveJob}
                  isSaved={!!savedJobMap[job.id]}
                  onApply={handleApply}
                  isApplied={appliedJobs.has(job.id)}
                  isActionLoading={actionLoading === job.id}
                />
              ))}
            </div>
          </div>

          <RecentlyViewedJobsWidget />
          <RecentApplicationsWidget />

          {/* EARLY ACCESS */}
          <div className="mt-6 relative" style={{ background: DS.paper, border: `1px solid ${DS.hairline}` }}>
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: DS.hairline }}>
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: ACCENT }} />
                <div>
                  <TechLabel style={{ color: ACCENT }}>03 · early access</TechLabel>
                  <h2 className="text-lg font-bold leading-tight mt-0.5" style={{ color: DS.forest }}>
                    Roles in pre-listing
                  </h2>
                </div>
              </div>
              <TechLabel style={{ color: `${DS.grid}99` }}>02 slots</TechLabel>
            </div>
            <div className="p-6">
              <p className="text-sm leading-relaxed mb-5" style={{ color: DS.grid }}>
                See what recruiters are searching for, before they post.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: DS.hairline }}>
                {["pending", "pending"].map((_, i) => (
                  <div
                    key={i}
                    className="h-24 flex items-center justify-center"
                    style={{ background: DS.paper }}
                  >
                    <TechLabel style={{ color: `${DS.grid}77` }}>
                      slot · 0{i + 1} · awaiting
                    </TechLabel>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT WIDGETS */}
        <div className="w-full lg:w-72 space-y-4">
          <SearchStatusWidget />
          <ScamWarningWidget />
          <RecruiterActivityWidget />
        </div>
      </div>
    </div>
  );
}
